import { useCallback, useRef, useState } from "react";
import { useRing } from "./ringContext";
import { useRingSet } from "./RingManagerModal";
import { serializeRingSet } from "./ringState";
import { saveDesign, type SaveResult } from "./designStore";

/**
 * "Save this design and put a link to it on the clipboard."
 *
 * Extracted from `ActionBar` when the floating Share button (v42) needed the
 * same behaviour: two copies of a save-then-share flow is exactly how a share
 * link and a save button end up writing different documents. The ActionBar's
 * Save Design and Share Link entries and the FAB all run THIS.
 *
 * Idempotent by content: re-sharing an unchanged design returns the link
 * already minted instead of writing a second record.
 *
 * v43: the document is the whole SET — every ring, the arrangement and the link —
 * so a shared pair opens as a pair. `serializeEngagementDesign` only ever returns
 * the ACTIVE ring, which is why this walks the list.
 */
export function useShareDesign() {
  const { r } = useRing();
  const set = useRingSet();
  const [busy, setBusy] = useState(false);
  const lastSaved = useRef<{ json: string; result: SaveResult } | null>(null);

  /**
   * Serialize → persist → put the token in the address bar → copy the link.
   * Resolves with the result (`mode` says whether it reached the Vault or fell
   * back to local storage); rejects with whatever the save threw, so the caller
   * owns the error message.
   */
  const share = useCallback(async (): Promise<SaveResult> => {
    setBusy(true);
    try {
      const design = await serializeRingSet(r, set);
      const json = JSON.stringify(design);
      const result =
        lastSaved.current?.json === json ? lastSaved.current.result : await saveDesign(design);
      lastSaved.current = { json, result };
      // replaceState, not push: sharing is not a navigation, and a back button
      // that undoes a save would be a lie.
      window.history.replaceState(null, "", result.shareUrl);
      await copyToClipboard(result.shareUrl);
      return result;
    } finally {
      setBusy(false);
    }
  }, [r, set]);

  return { share, busy };
}

/**
 * How long to wait for the async Clipboard API before using the synchronous
 * fallback. It is not a "slow network" allowance — `writeText` resolves in
 * microseconds when it resolves at all.
 */
const CLIPBOARD_TIMEOUT_MS = 1200;

/**
 * Clipboard write with the execCommand fallback for non-secure origins and
 * older embedded webviews (moved here with the rest of the share flow).
 *
 * The race is load-bearing, not belt-and-braces: `navigator.clipboard.writeText`
 * can NEVER SETTLE when the document does not have focus — observed live, with
 * the permission already granted. Awaiting it bare leaves the caller's promise
 * pending forever, which means a Share button stuck on its spinner and disabled
 * for the rest of the session, with no error anywhere. The design is already
 * saved and the URL already updated by this point, so the worst case after the
 * timeout is a link the user copies out of the address bar.
 */
export async function copyToClipboard(text: string): Promise<void> {
  try {
    if (navigator.clipboard?.writeText) {
      await Promise.race([
        navigator.clipboard.writeText(text),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("clipboard write timed out")), CLIPBOARD_TIMEOUT_MS),
        ),
      ]);
      return;
    }
  } catch {
    /* fall through to the execCommand fallback below */
  }
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.select();
  try {
    document.execCommand("copy");
  } catch {
    /* best-effort — clipboard just won't be populated */
  }
  document.body.removeChild(ta);
}
